import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, LifeBuoy, Plus, X, Headphones, Building2, ExternalLink } from 'lucide-react';
import { SuperAdminTicketView } from './SuperAdminTicketDetailPage';
import { useSidebarEdge } from '@/lib/useSidebarEdge';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { EmptyState } from '@/components/EmptyState';
import { saApi, useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  CATEGORIES,
  humanStatus,
  type Ticket,
} from '../support/supportCommon';
import { SelectField } from '@/components/ui/SelectField';

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.v, c.l.replace(' Question', '')]),
);

// Active scope tab — drives the page (list filter + create-button visibility).
type ActiveTab = 'platform' | 'platform_internal';

const TAB_CONFIG: Record<ActiveTab, {
  label: string;
  description: string;
  icon: typeof Building2;
  emptyTitle: string;
  emptyDesc: string;
}> = {
  platform: {
    label: 'Vendor Tickets',
    description: 'Tickets raised by tenants — product issues, billing, integration help.',
    icon: Headphones,
    emptyTitle: 'No vendor tickets',
    emptyDesc: 'Nothing from tenants right now.',
  },
  platform_internal: {
    label: 'Internal Team',
    description: 'Internal helpdesk — queries between you and the BrixLead platform team.',
    icon: Building2,
    emptyTitle: 'No internal tickets',
    emptyDesc: 'Use “New Internal Ticket” to raise a request to a teammate.',
  },
};

export function SuperAdminTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read active tab from URL (?tab=...) so back-navigation from detail page
  // returns the user to the same tab they came from. Default = vendor.
  const tabFromUrl = searchParams.get('tab');
  const initialTab: ActiveTab = tabFromUrl === 'platform_internal' ? 'platform_internal' : 'platform';

  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [showNewInternal, setShowNewInternal] = useState(false);
  // Ticket opened in the right-side drawer (row click).
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Keep URL in sync with active tab so refresh / back / share preserves it.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      return next;
    }, { replace: true });
  }, [activeTab, setSearchParams]);

  // Fetch the active scope's tickets. Clear the list first so the user
  // never sees stale rows from the other tab while the new fetch is in
  // flight. The `scope` param is captured for this run — we drop the
  // response if it doesn't match what's currently active when it lands.
  useEffect(() => {
    let cancelled = false;
    setTickets([]);
    setLoading(true);
    const scope = activeTab;
    const params: any = { scope };
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    saApi.get('/support/admin/tickets', { params })
      .then((res) => {
        if (cancelled || scope !== activeTab) return;
        setTickets(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        toast('error', err?.response?.data?.message || 'Failed to load tickets');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, filterStatus, filterPriority]);

  // Manual refresh used after creating a ticket etc.
  const fetchList = async () => {
    setLoading(true);
    try {
      const params: any = { scope: activeTab };
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const { data } = await saApi.get('/support/admin/tickets', { params });
      setTickets(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const cfg = TAB_CONFIG[activeTab];
  // Defensive second pass — only render rows whose scope matches the
  // active tab.
  const visibleTickets = tickets.filter((t) => t.scope === activeTab);

  const stats = {
    open: visibleTickets.filter((t) => t.status === 'open').length,
    inProgress: visibleTickets.filter((t) => t.status === 'in_progress' || t.status === 'pending').length,
    onHold: visibleTickets.filter((t) => t.status === 'on_hold').length,
    resolved: visibleTickets.filter((t) => t.status === 'resolved').length,
  };

  const { sorted, sort, onSort } = useSort(visibleTickets);

  return (
    <SuperAdminLayout title="Support Tickets">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-[#6366F1]" />
            Support Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vendor tickets + your team's internal helpdesk. Click any row to open.
          </p>
        </div>

        {/* Scope tabs — segmented control */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex mb-5 max-w-xl">
          {(['platform', 'platform_internal'] as ActiveTab[]).map((s) => {
            const c = TAB_CONFIG[s];
            const Icon = c.icon;
            const active = activeTab === s;
            return (
              <button
                key={s}
                onClick={() => setActiveTab(s)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? 'bg-[#6366F1] text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <Icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Stats — scoped to the active tab */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat label="Open" value={stats.open} color="blue" />
          <Stat label="In Progress" value={stats.inProgress} color="indigo" />
          <Stat label="On Hold" value={stats.onHold} color="amber" />
          <Stat label="Resolved" value={stats.resolved} color="emerald" />
        </div>

        {/* Action bar — description + status/priority filters + create (internal only) */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-xs text-slate-500">{cfg.description}</p>
          <div className="flex items-center gap-3">
            <SelectField
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]">
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{humanStatus(s)}</option>)}
            </SelectField>
            <SelectField
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]">
              <option value="">All Priorities</option>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </SelectField>
            {(filterStatus || filterPriority) && (
              <button onClick={() => { setFilterStatus(''); setFilterPriority(''); }}
                className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-500 hover:text-red-500">
                Clear
              </button>
            )}
            {activeTab === 'platform_internal' && (
              <button
                onClick={() => setShowNewInternal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold whitespace-nowrap">
                <Plus className="h-4 w-4" />
                New Internal Ticket
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">{cfg.label}</span>
            <span className="text-xs text-slate-400">
              {loading ? 'Loading…' : `${visibleTickets.length} ticket${visibleTickets.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
          ) : visibleTickets.length === 0 ? (
            <EmptyState icon={cfg.icon} title={cfg.emptyTitle} description={cfg.emptyDesc} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <TableHeadRow>
                    <SortTh label="S.No" sortable={false} className="whitespace-nowrap" />
                    <SortTh label="Ticket" sortKey="id" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="User" sortKey="userName" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Subject" sortKey="subject" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Category" sortKey="category" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Priority" sortKey="priority" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Assigned" sortKey="assignedAdminName" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                    <SortTh label="Updated" sortKey="updatedAt" sort={sort} onSort={onSort} className="whitespace-nowrap" />
                  </TableHeadRow>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {sorted.map((t, i) => (
                    <tr
                      key={t.id}
                      className="hover:bg-gray-50/80 cursor-pointer"
                      onClick={() => setOpenTicketId(t.id)}>
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">#{t.id.slice(-6).toUpperCase()}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-slate-700">{t.userName ?? '—'}</div>
                        <div className="text-xs text-slate-400">{t.userEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-800 max-w-[260px]">
                        <div className="truncate font-medium">{t.subject}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 capitalize whitespace-nowrap">{CATEGORY_LABEL[t.category] ?? t.category}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${PRIORITY_COLORS[t.priority]}`}>
                          {t.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status]}`}>
                          {humanStatus(t.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {t.assignedAdminName ?? <span className="text-slate-300">— Unassigned</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDateTime(t.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showNewInternal && (
          <NewInternalModal
            onClose={() => setShowNewInternal(false)}
            onSaved={() => { setShowNewInternal(false); fetchList(); }}
          />
        )}
      </div>
      {openTicketId && (
        <TicketDrawer
          ticketId={openTicketId}
          onClose={() => setOpenTicketId(null)}
          onOpenFull={() => navigate(`/super-admin/tickets/${openTicketId}`)}
          onChanged={() => fetchList()}
        />
      )}
    </SuperAdminLayout>
  );
}

// Ticket detail in a right-side drawer spanning up to the sidebar.
function TicketDrawer({ ticketId, onClose, onOpenFull, onChanged }: {
  ticketId: string;
  onClose: () => void;
  onOpenFull: () => void;
  onChanged: () => void;
}) {
  const left = useSidebarEdge();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex flex-col bg-[#F8F9FA] shadow-2xl border-l border-slate-200 animate-in slide-in-from-right duration-300"
      style={{ left }}
    >
      <div className="flex h-12 sm:h-14 flex-shrink-0 items-center justify-end gap-3 border-b border-slate-100 bg-white px-3 sm:px-6">
        <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6366F1] hover:underline">
          <ExternalLink className="h-4 w-4" /> Open full page
        </button>
        <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        <SuperAdminTicketView key={ticketId} ticketId={ticketId} embedded onClose={onClose} onChanged={onChanged} />
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: 'blue' | 'indigo' | 'amber' | 'emerald' }) {
  const palette: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${palette[color]}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// ── New internal ticket modal ───────────────────────────────────────────────
interface AdminUser { id: string; name: string; email?: string; role?: string }

function NewInternalModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { admin } = useSuperAdminAuth();
  const [form, setForm] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal',
    assignedAdminId: '',
    assignedAdminName: '',
  });
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState<AdminUser[]>([]);

  // Fetch super-admins so the requester can pre-assign the ticket.
  useEffect(() => {
    saApi.get('/super-admin/users')
      .then((res) => {
        const list: AdminUser[] = (res.data?.data ?? res.data ?? [])
          .map((u: any) => ({ id: u.id, name: u.name, email: u.email, role: u.role }));
        setAdmins(list);
      })
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast('error', 'Subject and message are required');
      return;
    }
    setSaving(true);
    try {
      const body: any = {
        subject: form.subject,
        message: form.message,
        category: form.category,
        priority: form.priority,
      };
      if (form.assignedAdminId) {
        body.assignedAdminId = form.assignedAdminId;
        body.assignedAdminName = form.assignedAdminName || null;
      }
      await saApi.post('/support/admin/tickets', body);
      toast('success', 'Internal ticket created');
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] placeholder:text-slate-400';
  const selectCls = `${inputCls} bg-white`;
  const labelCls = 'block text-xs font-medium text-slate-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-900">New Internal Ticket</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Subject *</label>
            <input className={inputCls} placeholder="Short summary"
              value={form.subject}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              maxLength={200} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <SelectField className={selectCls} value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </SelectField>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <SelectField className={selectCls} value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
                {PRIORITY_OPTIONS.map((v) =>
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                )}
              </SelectField>
            </div>
          </div>

          <div>
            <label className={labelCls}>Assign to (optional)</label>
            <SelectField
              className={selectCls}
              value={form.assignedAdminId}
              onChange={(e) => {
                const uid = e.target.value;
                const uname = uid ? admins.find((a) => a.id === uid)?.name ?? '' : '';
                setForm((p) => ({ ...p, assignedAdminId: uid, assignedAdminName: uname }));
              }}>
              <option value="">— Unassigned —</option>
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.role ? ` (${a.role})` : ''}{a.id === admin?.id ? ' — me' : ''}
                </option>
              ))}
            </SelectField>
            <p className="text-[11px] text-slate-400 mt-1">
              Pick a teammate to own this query. Can be reassigned later from the ticket page.
            </p>
          </div>

          <div>
            <label className={labelCls}>Message *</label>
            <textarea rows={6}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Describe the issue"
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              maxLength={10000} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={submit} disabled={saving || !form.subject.trim() || !form.message.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminTicketsPage;
